import { clsx } from "clsx"
import { Link } from "react-router-dom"

import { publishBlockedReason } from "~/api/battery-presentation-errors"
import { useExportBatteryBrochure } from "~/api/hooks/useBatteryPresentationShare"
import {
  type PdfExportStart,
  usePdfExportTab,
} from "~/containers/ProposalSetup/share/use-pdf-export-tab"

import { batterySetupStepPath } from "./setup-steps"

export const EXPORT_PDF_LABEL = `Export To PDF`
export const EXPORTING_LABEL = `Generating PDF`
export const EXPORT_RETRY_LABEL = `Try again`
export const EXPORT_FAILED_PROMPT = `Unable to generate the PDF.`
export const EXPORT_BLOCKED_POPUP_PROMPT = `Allow pop-ups for this site, then export again.`
export const EXPORT_NOT_CALCULATED_PROMPT = `Calculate System Pricing so this proposal has a saved financial summary before you export it.`
export const EXPORT_OUT_OF_DATE_PROMPT = `Pricing is out of date. Refresh Pricing so the PDF shows current amounts.`
export const OPEN_SYSTEM_PRICING_LABEL = `Open System Pricing`
export const EXPORT_TAB_CLOSED_PROMPT = `The tab was closed before the PDF was ready. Export again to open it.`

export type BatteryBrochureExportStart = PdfExportStart

export type BatteryBrochureExportAction = {
  readonly blockedReason: ReturnType<typeof publishBlockedReason>
  readonly failed: boolean
  readonly isLoading: boolean
  readonly popupBlocked: boolean
  readonly start: () => BatteryBrochureExportStart
  readonly tabClosed: boolean
}

/**
 * Opens the brochure tab in the same gesture as the click or key that asked
 * for it. The rail and quick search share one instance so a refusal shows in
 * whichever of them is on screen.
 */
export function useBatteryBrochureExportAction(
  proposalId: string,
): BatteryBrochureExportAction {
  const exportTab = usePdfExportTab(useExportBatteryBrochure(), `brochure`)
  const blockedReason = publishBlockedReason(exportTab.error)

  return {
    blockedReason,
    failed: exportTab.isError && blockedReason == null,
    isLoading: exportTab.isLoading,
    popupBlocked: exportTab.popupBlocked,
    start: () => exportTab.start(proposalId),
    tabClosed: exportTab.tabClosed,
  }
}

export function ExportBrochureAction({
  exportAction,
  proposalId,
}: {
  exportAction: BatteryBrochureExportAction
  proposalId: string
}): JSX.Element {
  const { blockedReason, failed, isLoading, popupBlocked, start, tabClosed } =
    exportAction

  return (
    // Matches the rail's part rows: same gutter and padding, so Export sits
    // level with Share instead of looking like a control bolted underneath.
    <div className="mx-[5px] flex flex-col gap-1 p-[7px]">
      <button
        type="button"
        data-testid="battery-setup-part-export-pdf"
        disabled={isLoading}
        onClick={() => {
          start()
        }}
        className={clsx(
          `font-display w-full text-left text-[0.875rem] leading-[21px] font-medium`,
          isLoading ? `cursor-progress text-bp-text-dim` : `text-bp-text`,
        )}
      >
        {isLoading ? EXPORTING_LABEL : EXPORT_PDF_LABEL}
      </button>
      {blockedReason ? (
        <div className="flex flex-col items-start gap-1">
          <p
            role="status"
            data-testid="battery-export-not-ready"
            className="text-[0.75rem] leading-tight text-bp-gold"
          >
            {blockedReason === `not_calculated`
              ? EXPORT_NOT_CALCULATED_PROMPT
              : EXPORT_OUT_OF_DATE_PROMPT}
          </p>
          <Link
            to={batterySetupStepPath(proposalId, `system-pricing`)}
            className="text-[0.75rem] leading-tight font-semibold text-bp-accent underline underline-offset-2"
          >
            {OPEN_SYSTEM_PRICING_LABEL}
          </Link>
        </div>
      ) : null}
      {popupBlocked ? (
        <p role="alert" className="text-[0.75rem] leading-tight text-bp-danger">
          {EXPORT_BLOCKED_POPUP_PROMPT}
        </p>
      ) : null}
      {tabClosed ? (
        <p role="alert" className="text-[0.75rem] leading-tight text-bp-danger">
          {EXPORT_TAB_CLOSED_PROMPT}
        </p>
      ) : null}
      {failed ? (
        <div className="flex flex-col items-start gap-1">
          <p
            role="alert"
            className="text-[0.75rem] leading-tight text-bp-danger"
          >
            {EXPORT_FAILED_PROMPT}
          </p>
          <button
            type="button"
            disabled={isLoading}
            onClick={() => {
              start()
            }}
            className="text-[0.75rem] leading-tight font-semibold text-bp-accent underline underline-offset-2 disabled:cursor-progress disabled:text-bp-text-dim"
          >
            {EXPORT_RETRY_LABEL}
          </button>
        </div>
      ) : null}
    </div>
  )
}
